/**
 * bateclub web OAuth client (PUBLIC client).
 *
 * One `BrowserOAuthClient` for both dev (loopback) and prod:
 *  - Loopback (localhost/127.0.0.1): atproto uses hardcoded metadata for
 *    `http://localhost` client_ids, so we synthesize the client_id with the
 *    redirect_uri + scope encoded in the query (per the atproto loopback spec).
 *  - Prod: a real client_id (the hosted oauth-client-metadata.json URL) +
 *    site-root redirect. `token_endpoint_auth_method: 'none'` (public).
 *
 * BrowserOAuthClient manages its own IndexedDB token/state stores and DPoP keys,
 * so there's nothing to persist in the app's own session schema beyond the DID.
 */

import {
  type AuthorizeOptions,
  type BrowserOAuthClient,
  type OAuthSession,
} from '@atproto/oauth-client-browser'

import {logger} from '#/logger'
import {
  OAUTH_CLIENT_ID,
  OAUTH_CLIENT_NAME,
  OAUTH_HANDLE_RESOLVER,
  OAUTH_REDIRECT_URI,
  OAUTH_SCOPE,
} from '#/config/oauth'

function isLoopback(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]'
}

let clientPromise: Promise<BrowserOAuthClient> | null = null

/**
 * Lazily construct the singleton BrowserOAuthClient. The @atproto/oauth-client-
 * browser package (and its @atproto/jwk dependency) is dynamically imported so
 * it is NOT pulled into the boot bundle -- a static import triggered a
 * circular-init crash (`zod_1.z.enum` undefined) during app startup. Public
 * client in both environments.
 */
export function getWebOAuthClient(): Promise<BrowserOAuthClient> {
  if (clientPromise) return clientPromise
  clientPromise = buildClient()
  return clientPromise
}

async function buildClient(): Promise<BrowserOAuthClient> {
  const {BrowserOAuthClient} = await import('@atproto/oauth-client-browser')
  let client: BrowserOAuthClient

  if (isLoopback()) {
    const port = window.location.port ? `:${window.location.port}` : ''
    const redirectUri = `http://127.0.0.1${port}/`
    const clientId =
      `http://localhost` +
      `?redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(OAUTH_SCOPE)}`
    client = new BrowserOAuthClient({
      handleResolver: OAUTH_HANDLE_RESOLVER,
      clientMetadata: {
        client_id: clientId,
        redirect_uris: [redirectUri],
        scope: OAUTH_SCOPE,
        token_endpoint_auth_method: 'none',
        response_types: ['code'],
        grant_types: ['authorization_code', 'refresh_token'],
        application_type: 'web',
        dpop_bound_access_tokens: true,
      },
      responseMode: 'fragment',
    })
  } else {
    client = new BrowserOAuthClient({
      handleResolver: OAUTH_HANDLE_RESOLVER,
      clientMetadata: {
        client_id: OAUTH_CLIENT_ID,
        client_name: OAUTH_CLIENT_NAME,
        client_uri: OAUTH_REDIRECT_URI,
        redirect_uris: [OAUTH_REDIRECT_URI],
        scope: OAUTH_SCOPE,
        token_endpoint_auth_method: 'none',
        response_types: ['code'],
        grant_types: ['authorization_code', 'refresh_token'],
        application_type: 'web',
        dpop_bound_access_tokens: true,
      },
      responseMode: 'fragment',
    })
  }

  return client
}

/**
 * Start "Sign in": resolve the handle's PDS and redirect there. Does not return
 * normally -- the browser navigates away; the callback lands at the site root
 * where App.web.tsx finishes login.
 */
export async function oauthSignIn(
  handle: string,
  options?: AuthorizeOptions,
): Promise<void> {
  const c = await getWebOAuthClient()
  await c.signInRedirect(handle.trim(), options)
}

/**
 * Complete an OAuth callback if the current URL carries callback params.
 * Returns the OAuthSession (+ optional state) or undefined if not a callback.
 */
export async function oauthInit(): Promise<
  {session: OAuthSession; state?: string | null} | undefined
> {
  const c = await getWebOAuthClient()
  return c.init()
}

/** Restore a previously-authorized session for a DID. */
export async function oauthRestore(did: string): Promise<OAuthSession> {
  const c = await getWebOAuthClient()
  return c.restore(did)
}

/**
 * Revoke a session's tokens server-side and delete the local copy. Best-effort;
 * the local delete happens even if the server call fails, so sign-out always
 * clears this device.
 */
export async function oauthRevoke(did: string): Promise<void> {
  try {
    const c = await getWebOAuthClient()
    await c.revoke(did)
  } catch (e) {
    logger.warn('oauth: revoke on sign-out failed', {
      safeMessage: e instanceof Error ? e.message : 'unknown',
    })
  }
}
